/**
 * Schoorsteenveger-funnel: Sessies → Aanvragen → Ingepland → Uitgevoerd → Omzet.
 *
 * Cohort-model: de aanvragen (GHL-opportunities) die in de gekozen periode zijn
 * aangemaakt, gevolgd door hoe ver dat cohort inmiddels in de pipeline zit.
 * Bij recente periodes zijn de latere fases per definitie nog laag — de
 * conversie komt hier later dan bij lead-gen.
 *
 * Hergebruikt de funnel-stat-rij uit het design-system (.funnel-row/.funnel-step-*);
 * het blauwe accent komt via .theme-blue-container in design-system.css.
 */

import React from 'react';
import { Users, ClipboardList, CalendarCheck, CheckCircle, Euro } from 'lucide-react';

const eur0 = (v) => new Intl.NumberFormat('nl-NL', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(v || 0);
const eur2 = (v) => new Intl.NumberFormat('nl-NL', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(v || 0);
const num = (v) => new Intl.NumberFormat('nl-NL').format(v || 0);
const pct1 = (n, d) => (d > 0 ? `${((n / d) * 100).toFixed(1)}%` : null);
const pct0 = (n, d) => (d > 0 ? `${((n / d) * 100).toFixed(0)}%` : null);

// Stat-kaart: icoon links, en in de tekstkolom het label BOVEN het getal
// (houdt de kaart smal), met de meta-regels eronder.
const FunnelStep = ({ icon: Icon, label, value, meta, metaTitle, isLast }) => (
  <div className="funnel-step-wrapper">
    <div className="kpi-card funnel-step-card" title={metaTitle || undefined}>
      <div className="funnel-step-icon">
        <Icon size={18} strokeWidth={2.5} />
      </div>
      <div className="funnel-step-meta">
        <span className="funnel-step-label">{label}</span>
        <span className="funnel-step-value">{value}</span>
        {(Array.isArray(meta) ? meta : (meta ? [meta] : [])).map((line, i) => (
          <span key={i} className="funnel-step-ratio">{line}</span>
        ))}
      </div>
    </div>
    {!isLast && <div className="funnel-arrow">→</div>}
  </div>
);

const SvFunnelSection = ({ data, loading }) => {
  if (loading && !data) {
    return (
      <div className="funnel-row" style={{ minHeight: '68px' }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="funnel-step-wrapper">
            <div className="kpi-card funnel-step-card animate-pulse" style={{ minHeight: '68px' }} />
            {i < 4 && <div className="funnel-arrow">→</div>}
          </div>
        ))}
      </div>
    );
  }
  if (!data) return null;

  const { sessions = 0, sessionsClamped = false, aanvragen = {}, ingepland = {}, uitgevoerd = {}, omzet = {} } = data;

  const cohortTitle = 'Cohort: aanvragen die in de gekozen periode zijn aangemaakt; latere fases tonen hoe ver dit cohort nu in de pipeline is';

  const sessionsMeta = sessionsClamped ? ['tracking sinds 26 apr 2026'] : null;
  const aanvragenMeta = [
    pct1(aanvragen.count, sessions) && `${pct1(aanvragen.count, sessions)} van sessies`,
    aanvragen.value > 0 && `${eur0(aanvragen.value)} aangevraagd`,
  ].filter(Boolean);
  const ingeplandMeta = pct0(ingepland.count, aanvragen.count)
    ? [`${pct0(ingepland.count, aanvragen.count)} van aanvragen`] : null;
  // Bij jonge cohorten (vandaag/deze week) is uitgevoerd vrijwel altijd nog 0:
  // de uitvoering volgt dagen tot weken na de aanvraag. "Nog onderweg" maakt
  // dat expliciet — leeg betekent jong, niet verloren.
  const uitgevoerdMeta = [
    pct0(uitgevoerd.count, aanvragen.count) && `${pct0(uitgevoerd.count, aanvragen.count)} van aanvragen`,
    (uitgevoerd.onderweg || 0) > 0 && `${uitgevoerd.onderweg} nog onderweg`,
  ].filter(Boolean);
  const omzetMeta = [
    aanvragen.count > 0 && `${eur0((omzet.value || 0) / aanvragen.count)} per aanvraag`,
    sessions > 0 && `${eur2((omzet.value || 0) / sessions)} per sessie`,
  ].filter(Boolean);

  return (
    <div className="funnel-row" style={{ marginBottom: 'var(--space-6)' }}>
      <FunnelStep
        icon={Users}
        label="SESSIES"
        value={num(sessions)}
        meta={sessionsMeta}
        metaTitle={sessionsClamped ? 'Server-side sessie-tracking bestaat sinds 26 apr 2026; oudere periodes tellen vanaf die datum' : null}
        isLast={false}
      />
      <FunnelStep
        icon={ClipboardList}
        label="AANVRAGEN"
        value={num(aanvragen.count)}
        meta={aanvragenMeta}
        metaTitle={cohortTitle}
        isLast={false}
      />
      <FunnelStep
        icon={CalendarCheck}
        label="INGEPLAND"
        value={num(ingepland.count)}
        meta={ingeplandMeta}
        metaTitle={cohortTitle}
        isLast={false}
      />
      <FunnelStep
        icon={CheckCircle}
        label="UITGEVOERD"
        value={num(uitgevoerd.count)}
        meta={uitgevoerdMeta}
        metaTitle={`${cohortTitle}. De uitvoering volgt dagen tot weken na de aanvraag — bij een korte recente periode is dit vrijwel altijd nog laag; "nog onderweg" = open aanvragen die nog niet zijn uitgevoerd.`}
        isLast={false}
      />
      <FunnelStep
        icon={Euro}
        label="OMZET"
        value={eur0(omzet.value)}
        meta={omzetMeta}
        metaTitle={'Omzet van dit cohort (Cash Flow-fase of verder). '
          + '"Per aanvraag" = omzet ÷ álle aanvragen: stijgt wanneer de conversie '
          + 'aanvraag→uitgevoerd verbetert of de gemiddelde klusprijs stijgt. '
          + '"Per sessie" = bezoekerswaarde (CRO/rankings). Gem. bedrag per factuur staat als KPI bovenaan.'}
        isLast={true}
      />
    </div>
  );
};

export default SvFunnelSection;
